import { useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Eraser } from 'lucide-react';

/**
 * Touchscreen / mouse signature pad. Outputs a base64 PNG data URL via onChange.
 * Pure HTML5 canvas — no library deps. Pointer events cover mouse + touch + pen.
 */
export default function SignaturePad({ value, onChange, disabled = false, height = 140, testId = 'signature-pad' }) {
  const canvasRef = useRef(null);
  const widthRef = useRef(0);
  // Loop W3 — true while a cross-origin (presigned R2) image is painted:
  // the canvas is tainted, so toDataURL() would throw. Never composite a
  // new drawing over it — the first stroke / Clear starts a fresh canvas.
  const taintedRef = useRef(false);
  const valueRef = useRef(value);
  valueRef.current = value;
  const [drawing, setDrawing] = useState(false);
  const drawingRef = useRef(false);
  drawingRef.current = drawing;
  const [empty, setEmpty] = useState(!value);

  // (Re)size the bitmap to the container and reset to a blank white pad.
  // Assigning canvas.width also drops any cross-origin taint.
  const resetCanvas = () => {
    const canvas = canvasRef.current;
    const ratio = window.devicePixelRatio || 1;
    const rect = canvas.parentElement.getBoundingClientRect();
    widthRef.current = rect.width;
    canvas.width = Math.floor(rect.width * ratio);
    canvas.height = Math.floor(height * ratio);
    canvas.style.height = `${height}px`;
    canvas.style.width = '100%';
    const ctx = canvas.getContext('2d');
    ctx.scale(ratio, ratio);
    ctx.lineWidth = 2.2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = '#0a0a0c';
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    taintedRef.current = false;
    return { ctx, rect };
  };

  // Initialize canvas size & seed image (if any)
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const { ctx, rect } = resetCanvas();

    // iter-156gj — Locked-report signature restoration.
    // Before iter-156fr, every saved signature was a `data:image/png;base64,...`
    // data URL that we could hand straight to `Image.src`. The R2
    // base64 backfill replaced those inline blobs with `{r2_key, ...}`
    // dicts on the server, and the GET resolver now returns a
    // short-lived presigned `https://…r2.cloudflarestorage.com/…`
    // URL in `signatures.<key>.data_url`. The prior check
    // (`value.startsWith('data:image/')`) silently dropped every
    // presigned URL — that's why the canvas shows EMPTY on locked
    // reports while the "Signed at" timestamp + signer name still
    // render. Accept both shapes here. We deliberately do NOT set
    // `crossOrigin` so that the browser falls back to a no-cors
    // image load (R2 presigned GETs don't add Access-Control-Allow-Origin
    // headers); the canvas gets tainted — `taintedRef` makes the first
    // stroke / Clear start over, so `toDataURL()` never runs on it.
    if (value && (value.startsWith('data:image/') || /^https?:\/\//i.test(value))) {
      const img = new Image();
      img.onload = () => {
        ctx.drawImage(img, 0, 0, rect.width, height);
        taintedRef.current = !value.startsWith('data:image/');
        setEmpty(false);
      };
      img.onerror = () => {
        // Presign expired or R2 key missing — leave the pad empty
        // rather than crash. The "Locked" label + signed_at still
        // tell the user the signature exists in the audit trail.
      };
      img.src = value;
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Loop W3 — rotation / resize: re-fit the bitmap to the new width and
  // repaint the current signature at its original size (no stretching).
  useEffect(() => {
    const onResize = () => {
      const canvas = canvasRef.current;
      if (!canvas || drawingRef.current) return;
      const prevWidth = widthRef.current;
      if (Math.abs(canvas.parentElement.getBoundingClientRect().width - prevWidth) < 1) return;
      let src = '';
      if (taintedRef.current) src = valueRef.current || '';
      else {
        try { src = canvas.toDataURL('image/png'); } catch { src = ''; }
      }
      const tainted = taintedRef.current;
      const { ctx } = resetCanvas();
      if (!src) return;
      const img = new Image();
      img.onload = () => {
        ctx.drawImage(img, 0, 0, prevWidth, height);
        taintedRef.current = tainted;
      };
      img.src = src;
    };
    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', onResize);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const getXY = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX || (e.touches && e.touches[0]?.clientX) || 0) - rect.left;
    const y = (e.clientY || (e.touches && e.touches[0]?.clientY) || 0) - rect.top;
    return { x, y };
  };

  const start = (e) => {
    if (disabled) return;
    e.preventDefault();
    if (taintedRef.current) resetCanvas();
    const ctx = canvasRef.current.getContext('2d');
    const { x, y } = getXY(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
    setDrawing(true);
  };

  const move = (e) => {
    if (!drawing || disabled) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext('2d');
    const { x, y } = getXY(e);
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const end = () => {
    if (!drawing) return;
    setDrawing(false);
    const dataUrl = canvasRef.current.toDataURL('image/png');
    setEmpty(false);
    onChange?.(dataUrl);
  };

  const clear = () => {
    if (disabled) return;
    resetCanvas();
    setEmpty(true);
    onChange?.('');
  };

  return (
    <div className="space-y-2" data-testid={testId}>
      <div
        className="relative rounded-md border border-border bg-white overflow-hidden"
        style={{ touchAction: 'none' }}
      >
        <canvas
          ref={canvasRef}
          onPointerDown={start}
          onPointerMove={move}
          onPointerUp={end}
          onPointerLeave={end}
          onTouchStart={start}
          onTouchMove={move}
          onTouchEnd={end}
          data-testid={`${testId}-canvas`}
        />
        {empty && !disabled && (
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center text-[#71717a] text-sm italic">
            Sign here with your finger or mouse
          </div>
        )}
      </div>
      <div className="flex justify-between items-center text-xs text-muted-foreground">
        <span>{disabled ? 'Locked' : 'Sign above'}</span>
        {!disabled && (
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={clear}
            data-testid={`${testId}-clear`}
            className="text-muted-foreground hover:text-foreground h-7"
          >
            <Eraser className="w-3 h-3 mr-1" /> Clear
          </Button>
        )}
      </div>
    </div>
  );
}
